import { isEmptyCustomFieldValue } from '../forms/custom-field-values';
import { canViewCustomField } from './custom-field-entity-types';

/**
 * Custom field "surfaces" (PD-15892): places outside a record's own editor
 * where a custom field may also be filled in. A definition opts in through its
 * `surfaces` JSON column, e.g. `{ "cartSidebar": true }`.
 *
 * The first surface is the CRM cart sidebar: an order field shown there is
 * filled in while the cart is built, before the order exists as anything but a
 * cart. Pharmsource's "CSOS and 222 Form" field is the first one.
 */
export const CUSTOM_FIELD_SURFACES = ['cartSidebar'] as const;

export type CustomFieldSurface = (typeof CUSTOM_FIELD_SURFACES)[number];

/** The `custom_field_definition.surfaces` column. */
export type CustomFieldSurfacesModel = Partial<
	Record<CustomFieldSurface, boolean>
>;

/**
 * The entity types each surface can show. The cart sidebar edits the cart,
 * which is an order, so only order fields can be placed on it.
 */
export const CUSTOM_FIELD_SURFACE_ENTITY_TYPES: Readonly<
	Record<CustomFieldSurface, ReadonlyArray<string>>
> = {
	cartSidebar: ['order'],
};

const KNOWN_SURFACES: ReadonlySet<string> = new Set(CUSTOM_FIELD_SURFACES);

/**
 * Why a `surfaces` value cannot be stored for a field of `entityType`, or null
 * when it can. Null and undefined mean "no surfaces" and are always accepted.
 * The column is free-form JSON, so an unknown key or a non-boolean value is
 * refused here rather than stored and silently ignored.
 */
export function describeCustomFieldSurfacesError(
	surfaces: unknown,
	entityType: string,
): string | null {
	if (surfaces == null) {
		return null;
	}
	if (typeof surfaces !== 'object' || Array.isArray(surfaces)) {
		return 'surfaces must be an object';
	}
	for (const [key, value] of Object.entries(surfaces)) {
		if (!KNOWN_SURFACES.has(key)) {
			return `Unknown custom field surface "${key}"`;
		}
		if (typeof value !== 'boolean') {
			return `The custom field surface "${key}" must be true or false`;
		}
		if (
			value &&
			!CUSTOM_FIELD_SURFACE_ENTITY_TYPES[key as CustomFieldSurface].includes(
				entityType,
			)
		) {
			return `The custom field surface "${key}" is only available for ${CUSTOM_FIELD_SURFACE_ENTITY_TYPES[
				key as CustomFieldSurface
			].join(', ')} fields`;
		}
	}
	return null;
}

/**
 * A valid `surfaces` value as it should be stored: only the surfaces that are
 * on, and null when none are, so "off everywhere" has one spelling.
 */
export function normalizeCustomFieldSurfaces(
	surfaces: CustomFieldSurfacesModel | null | undefined,
): CustomFieldSurfacesModel | null {
	if (surfaces == null) {
		return null;
	}
	const normalized: CustomFieldSurfacesModel = {};
	for (const surface of CUSTOM_FIELD_SURFACES) {
		if (surfaces[surface] === true) {
			normalized[surface] = true;
		}
	}
	return Object.keys(normalized).length > 0 ? normalized : null;
}

/** The definition fields the surface rules read. The entity and its DTO both fit. */
export interface CustomFieldSurfaceDefinitionModel {
	fieldKey: string;
	label: string;
	entityType: string;
	isActive: boolean | number;
	isRequired: boolean | number;
	allowedRoles: Array<number> | null;
	surfaces?: CustomFieldSurfacesModel | null;
}

/** tinyint columns arrive as 1/0 unless the column is typed boolean. */
function isFlagSet(flag: unknown): boolean {
	return flag === true || flag === 1 || flag === '1';
}

/**
 * Whether a definition is shown on a surface: an active field that opted in,
 * of an entity type the surface can show. Role visibility is checked
 * separately (canViewCustomField), because the server and the screen get the
 * user's roles in different ways.
 */
export function isCustomFieldOnSurface(
	definition: CustomFieldSurfaceDefinitionModel,
	surface: CustomFieldSurface,
): boolean {
	return (
		isFlagSet(definition.isActive) &&
		definition.surfaces?.[surface] === true &&
		CUSTOM_FIELD_SURFACE_ENTITY_TYPES[surface].includes(definition.entityType)
	);
}

/**
 * Keys the platform itself writes into `order.customFields`: the licensing
 * snapshot and acknowledgement stamped at submit, the order-day license check
 * state, and the storefront hide-pricing flag. A custom field definition with
 * one of these keys would let a screen overwrite or clear them, so a definition
 * may not use them (compared case-insensitively, because MySQL JSON paths are
 * case-sensitive but people are not).
 */
export const ORDER_CUSTOM_FIELD_SYSTEM_KEYS: ReadonlyArray<string> = [
	'licenseSnapshots',
	'licenseGoodStandingAck',
	'orderLicenseCheck',
	'portalHidePricing',
];

const RESERVED_ORDER_KEYS: ReadonlySet<string> = new Set(
	ORDER_CUSTOM_FIELD_SYSTEM_KEYS.map((key) => key.toLowerCase()),
);

/** Whether a field key is reserved for a system value on this entity type. */
export function isReservedCustomFieldKey(
	entityType: string,
	fieldKey: string,
): boolean {
	return (
		entityType === 'order' && RESERVED_ORDER_KEYS.has(fieldKey.toLowerCase())
	);
}

/**
 * The part of a cart custom-field write the caller is allowed to make
 * (PD-15892). A key survives only when it names an active order field placed on
 * the cart sidebar that the caller's roles can see; everything else — system
 * keys, fields not on the sidebar, role-restricted fields, removed fields — is
 * dropped and reported, so the endpoint can never become a way to write the
 * rest of `order.customFields`. Values are passed through unchanged (null
 * included, which clears the key); their rules are checked afterwards by the
 * shared custom field validator.
 */
export function filterCartCustomFieldsPatch(
	definitions: ReadonlyArray<CustomFieldSurfaceDefinitionModel>,
	patch: Record<string, unknown>,
	userRoleIds: ReadonlyArray<number>,
): { accepted: Record<string, unknown>; dropped: Array<string> } {
	const allowed = new Set(
		definitions
			.filter(
				(definition) =>
					isCustomFieldOnSurface(definition, 'cartSidebar') &&
					!isReservedCustomFieldKey(definition.entityType, definition.fieldKey) &&
					canViewCustomField(definition.allowedRoles, userRoleIds),
			)
			.map((definition) => definition.fieldKey),
	);

	const accepted: Record<string, unknown> = {};
	const dropped: Array<string> = [];
	for (const [key, value] of Object.entries(patch)) {
		if (allowed.has(key)) {
			accepted[key] = value;
		} else {
			dropped.push(key);
		}
	}
	return { accepted, dropped };
}

/**
 * Apply a patch to a stored custom-fields object the way MySQL's
 * JSON_MERGE_PATCH applies a flat patch: a key set to null is removed, any
 * other value replaces the stored one, and keys the patch does not name are
 * kept untouched. The stored object is not modified.
 */
export function mergeCustomFieldsPatch(
	existing: Record<string, unknown> | null | undefined,
	patch: Record<string, unknown>,
): Record<string, unknown> {
	const merged: Record<string, unknown> = { ...(existing ?? {}) };
	for (const [key, value] of Object.entries(patch)) {
		if (value === null) {
			delete merged[key];
		} else if (value !== undefined) {
			merged[key] = value;
		}
	}
	return merged;
}

/**
 * The required cart-sidebar fields an order is missing, for the user placing it
 * (PD-15892). A field only blocks Place Order when it is on the sidebar, active,
 * required, and visible to that user: a role that cannot see a field cannot fill
 * it in either.
 */
export function findMissingRequiredCartCustomFields(
	definitions: ReadonlyArray<CustomFieldSurfaceDefinitionModel>,
	values: Record<string, unknown> | null | undefined,
	userRoleIds: ReadonlyArray<number>,
): Array<{ fieldKey: string; label: string }> {
	const stored = values ?? {};
	return definitions
		.filter(
			(definition) =>
				isCustomFieldOnSurface(definition, 'cartSidebar') &&
				isFlagSet(definition.isRequired) &&
				canViewCustomField(definition.allowedRoles, userRoleIds) &&
				isEmptyCustomFieldValue(
					Object.prototype.hasOwnProperty.call(stored, definition.fieldKey)
						? stored[definition.fieldKey]
						: undefined,
				),
		)
		.map((definition) => ({
			fieldKey: definition.fieldKey,
			label: definition.label,
		}));
}
